import React from 'react';

import { Alert, Button, Stack, Typography } from '@mui/material';

import { useUnwatchTicketRisk, useWatchTicketRisk } from '~/hooks/apis/support/useTicketRiskWatch';

import { type CxTicketRow, formatShortDateTime } from './ticketRiskRows';

interface Props {
  ticket: CxTicketRow;
}

/**
 * Who is handling this ticket, and the signed-in leader's control: watch it, take it over, or
 * stop. One watcher at a time — taking over releases the previous one, and svc-support records
 * who from the token, so nothing about the user is sent.
 */
export default function TicketWatchPanel({ ticket }: Props) {
  const watch = useWatchTicketRisk();
  const unwatch = useUnwatchTicketRisk();
  const isPending = watch.isPending || unwatch.isPending;
  const error = watch.error ?? unwatch.error;
  const { watcher } = ticket;

  return (
    <Stack spacing={1.5}>
      {watcher ? (
        <Typography variant="body2">
          <strong>
            {watcher.watcherName}
            {ticket.isWatchedByMe ? ' (you)' : ''}
          </strong>
          {` is watching · since ${formatShortDateTime(watcher.createdAt)}`}
        </Typography>
      ) : (
        <Typography color="text.disabled">Nobody is watching this ticket yet</Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        {!watcher && (
          <Button
            variant="contained"
            size="small"
            disabled={isPending}
            onClick={() => watch.mutate({ ticketId: ticket.id })}
          >
            Watch this ticket
          </Button>
        )}
        {watcher && ticket.isWatchedByMe && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={isPending}
            onClick={() => unwatch.mutate({ ticketId: ticket.id })}
          >
            Stop watching
          </Button>
        )}
        {watcher && !ticket.isWatchedByMe && (
          <>
            <Button
              variant="contained"
              color="warning"
              size="small"
              disabled={isPending}
              onClick={() => watch.mutate({ ticketId: ticket.id })}
            >
              Take over
            </Button>
            <Typography variant="caption" color="text.secondary">
              This makes you the watcher instead of {watcher.watcherName}.
            </Typography>
          </>
        )}
      </Stack>

      {error && (
        <Alert severity="error">{error instanceof Error ? error.message : 'Could not update who is watching'}</Alert>
      )}
    </Stack>
  );
}
